import React, { useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import PropTypes from "prop-types"
import { Button, Card, Checkbox, Dropdown, Empty, Flex, Spin, Tag, Typography } from "antd"
import { MoreOutlined, PlusOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { setFormTouched, setFormValues } from "store/proNewListingForms"
import { useAddOns } from "pagesV2/pro/Settings/AddOns/hooks/useAddOns"
import AddOnModal from "pagesV2/pro/Settings/AddOns/modal/AddOnModal"
import { NewConfirmModal } from "ui/NewConfirmModal"
import AddOnPhoto from "ui/AddOnPhoto"
import { spacings } from "themes/variables"
import styles from "./AddOns.module.css"

const { Text } = Typography

const CONFIRM_COPY = {
  delete: { titleKey: "settings.add_ons.delete_title", messageKey: "settings.add_ons.delete_message", confirmKey: "settings.action_delete", titleDefault: "Delete Add-on?", messageDefault: "Are you sure you want to delete:", confirmDefault: "Delete" },
  deactivate: { titleKey: "settings.add_ons.deactivate_title", messageKey: "settings.add_ons.deactivate_message", confirmKey: "settings.action_deactivate", titleDefault: "Deactivate Add-on?", messageDefault: "Are you sure you want to deactivate:", confirmDefault: "Deactivate" },
  activate: { titleKey: "settings.add_ons.activate_title", messageKey: "settings.add_ons.activate_message", confirmKey: "settings.action_activate", titleDefault: "Activate Add-on?", messageDefault: "Are you sure you want to activate:", confirmDefault: "Activate" }
}

const AddOnRow = ({ addOn, checked, onChange, menuItems, isMutating }) => {
  const { t } = useTranslation()
  return (
    <div className={styles.row}>
      <Flex justify="space-between" align="center" gap={spacings.S}>
        <Checkbox checked={checked} onChange={(e) => onChange(addOn.id, e.target.checked)} disabled={!addOn.enabled}>
          <Flex align="center" gap={spacings.S}>
            <AddOnPhoto src={addOn.photo_url} alt={addOn.title} size={40} iconSize={18} />
            <Flex vertical gap={2}>
              <Flex align="center" gap={8}>
                <Text strong>{addOn.title}</Text>
                {!addOn.enabled && <Tag color="default">{t("settings.status_inactive")}</Tag>}
              </Flex>
              <Text className={styles.infoCategory}>{addOn.add_on_category?.name ?? ""}</Text>
            </Flex>
          </Flex>
        </Checkbox>
        <Flex align="center" gap={spacings.S}>
          {(addOn.min_per_order != null || addOn.max_per_order != null) && (
            <Text className={styles.rangeText}>
              {t("experiences.add_ons.range", {
                min: addOn.min_per_order ?? 0,
                max: addOn.max_per_order ?? "∞",
                defaultValue: `Min ${addOn.min_per_order ?? 0} - Max ${addOn.max_per_order ?? "∞"}`
              })}
            </Text>
          )}
          <Text strong className={styles.price}>{`$${Number(addOn.price ?? 0).toFixed(2)}`}</Text>
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight" disabled={isMutating}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Flex>
      </Flex>
    </div>
  )
}

AddOnRow.propTypes = {
  addOn: PropTypes.object.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  menuItems: PropTypes.array.isRequired,
  isMutating: PropTypes.bool.isRequired
}

export const AddOns = ({ form, isFormInitialized }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { id: listingId } = useParams()
  const detailsFormValues = useSelector((state) => state.proNewListingForms?.details?.values)

  const {
    addOns,
    addOnsLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isMutating,
    isModalOpen,
    addOnToEdit,
    handleAddAddOn,
    openAddModal,
    closeModal,
    getMenuItems,
    confirmAction,
    closeConfirm,
    handleConfirm
  } = useAddOns({ enabled: true })

  const [selectedAddOnIds, setSelectedAddOnIds] = useState(() => form.getFieldValue("add_on_ids") || [])

  useEffect(() => {
    if (isFormInitialized) {
      setSelectedAddOnIds(form.getFieldValue("add_on_ids") || [])
    }
  }, [isFormInitialized, form])

  // Track the previously-seen server-side association state per add-on so we
  // can react when the backend adds or removes this listing from an add-on's
  // associated_trips (without overriding the user's own toggles in between).
  const prevAssociationsRef = useRef(new Map())

  useEffect(() => {
    if (!isFormInitialized || addOns.length === 0) return
    const activeIds = new Set(addOns.filter((a) => a.enabled).map((a) => a.id))
    const listingIdNum = Number(listingId)
    const toSelect = []
    const toDeselect = []
    for (const addOn of addOns) {
      if (!Number.isFinite(listingIdNum)) break
      const currentlyAssociated =
        addOn.enabled && (addOn.associated_trips || []).some((trip) => Number(trip.id) === listingIdNum)
      const prevAssociated = prevAssociationsRef.current.get(addOn.id)
      if (prevAssociated === undefined) {
        if (currentlyAssociated) toSelect.push(addOn.id)
      } else if (prevAssociated !== currentlyAssociated) {
        if (currentlyAssociated) toSelect.push(addOn.id)
        else toDeselect.push(addOn.id)
      }
      prevAssociationsRef.current.set(addOn.id, currentlyAssociated)
    }
    setSelectedAddOnIds((prev) => {
      const removeSet = new Set(toDeselect)
      const kept = prev.filter((aid) => activeIds.has(aid) && !removeSet.has(aid))
      const missing = toSelect.filter((aid) => !kept.includes(aid))
      if (missing.length === 0 && kept.length === prev.length) return prev
      const next = [...kept, ...missing]
      form.setFieldValue("add_on_ids", next)
      return next
    })
  }, [isFormInitialized, addOns, listingId, form])

  const handleToggle = (addOnId, checked) => {
    setSelectedAddOnIds((prev) => {
      const next = checked ? [...prev, addOnId] : prev.filter((id) => id !== addOnId)
      form.setFieldValue("add_on_ids", next)
      // Mirror into Redux details.values — that's what useTabSwitchSave reads from.
      dispatch(setFormValues("details", { ...(detailsFormValues || {}), add_on_ids: next }))
      dispatch(setFormTouched("details", true))
      return next
    })
  }

  const copy = confirmAction ? CONFIRM_COPY[confirmAction.type] : null

  return (
    <>
      <Spin spinning={addOnsLoading}>
        <Card
          title={t("experiences.add_ons.section_title")}
          className="mb-3xl"
          extra={
            <Button color="primary" variant="solid" icon={<PlusOutlined />} onClick={openAddModal}>
              {t("experiences.add_ons.create_new")}
            </Button>
          }
        >
          <Text className={styles.description}>
            {t("experiences.add_ons.description", { defaultValue: "Select the add-ons your guests can purchase with this experience." })}
          </Text>
          {!addOnsLoading && addOns.length === 0 ? (
            <Empty description={t("experiences.add_ons.empty_title")} className={styles.empty} />
          ) : (
            <Flex vertical gap={spacings.XS} className={`mt-m ${styles.scrollList}`}>
              {addOns.map((addOn) => (
                <AddOnRow
                  key={addOn.id}
                  addOn={addOn}
                  checked={selectedAddOnIds.includes(addOn.id)}
                  onChange={handleToggle}
                  menuItems={getMenuItems(addOn).filter(
                    (item) => item.key !== "manage-experiences" && !(item.key === "toggle" && addOn.enabled)
                  )}
                  isMutating={isMutating}
                />
              ))}
            </Flex>
          )}
        </Card>
      </Spin>

      <AddOnModal
        visible={isModalOpen}
        onCancel={closeModal}
        onSave={handleAddAddOn}
        initialValues={addOnToEdit}
        isSaving={isCreating || isUpdating}
      />
      <NewConfirmModal
        visible={!!confirmAction}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        title={copy ? t(copy.titleKey, { defaultValue: copy.titleDefault }) : ""}
        message={copy ? t(copy.messageKey, { defaultValue: copy.messageDefault }) : ""}
        highlightText={confirmAction?.addOn?.title}
        confirmLabel={copy ? t(copy.confirmKey, { defaultValue: copy.confirmDefault }) : ""}
        loading={isDeleting || isUpdating}
      />
    </>
  )
}

AddOns.propTypes = {
  form: PropTypes.object.isRequired,
  isFormInitialized: PropTypes.bool
}

AddOns.defaultProps = {
  isFormInitialized: false
}
